import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import './DealerToast.css'

type ToastType = 'success' | 'error' | 'info'

interface Toast {
  id: number
  message: string
  type: ToastType
}

interface DealerToastContextValue {
  showToast: (message: string, type?: ToastType) => void
}

const DealerToastContext = createContext<DealerToastContextValue | null>(null)

const TOAST_TTL_MS = 2500

// один тост за раз: новое сообщение заменяет предыдущее, чтобы частые «Скопировано» не копились
export function DealerToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null)
  const timerRef = useRef<number>()

  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    window.clearTimeout(timerRef.current)
    setToast({ id: Date.now(), message, type })
    timerRef.current = window.setTimeout(() => setToast(null), TOAST_TTL_MS)
  }, [])

  useEffect(() => () => window.clearTimeout(timerRef.current), [])

  return (
    <DealerToastContext.Provider value={{ showToast }}>
      {children}
      <div className="dealer-toast-region" role="status" aria-live="polite">
        {toast && (
          <div key={toast.id} className={`dealer-toast dealer-toast--${toast.type}`}>
            {toast.type === 'success' && (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
            {toast.message}
          </div>
        )}
      </div>
    </DealerToastContext.Provider>
  )
}

export function useDealerToast() {
  const ctx = useContext(DealerToastContext)
  if (!ctx) throw new Error('useDealerToast must be used within DealerToastProvider')
  return ctx
}
